/* Owns identifier presentation and the free-text parsing behind the command
   palette, the ban dialog's paste box and the lookup page. The wire-level rules
   (bare values, first-colon split, hard vs soft types) come from
   @sentinelac/shared and are never reimplemented here. */

import {
  formatIdentifier,
  HARD_IDENTIFIER_TYPES,
  IDENTIFIER_TYPES,
  isHardIdentifier,
  maskIdentifier,
  parseIdentifierInput,
  type Identifiers,
  type IdentifierType,
} from '@sentinelac/shared'

export { formatIdentifier, HARD_IDENTIFIER_TYPES, IDENTIFIER_TYPES, isHardIdentifier, maskIdentifier }
export type { Identifiers, IdentifierType }

/** `player_identifiers.type` also carries opaque hardware tokens, which chips render. */
export type ChipIdentifierType = IdentifierType | 'token'

/** Full class names: Tailwind scans source text and cannot resolve a runtime concat. */
export const IDENTIFIER_TEXT_CLASS: Record<ChipIdentifierType, string> = {
  license: 'text-id-license',
  license2: 'text-id-license2',
  steam: 'text-id-steam',
  discord: 'text-id-discord',
  fivem: 'text-id-fivem',
  ip: 'text-id-ip',
  token: 'text-id-token',
}

export const IDENTIFIER_BORDER_CLASS: Record<ChipIdentifierType, string> = {
  license: 'border-id-license/40',
  license2: 'border-id-license2/40',
  steam: 'border-id-steam/40',
  discord: 'border-id-discord/40',
  fivem: 'border-id-fivem/40',
  ip: 'border-id-ip/40',
  token: 'border-id-token/40',
}

const BAN_CODE_PATTERN = /^SAC-[0-9A-Z]{1,16}$/

/** What the Ctrl+K palette and the lookup field can make of one pasted line. */
export type PaletteInput =
  | { kind: 'identifier'; type: IdentifierType; value: string; expression: string }
  | { kind: 'banCode'; code: string }
  | null

/**
 * Normalises whatever a staff member pasted. Order matters: the explicit
 * `type:value` form always wins, then the shapes that are unambiguous.
 *
 * The Steam64 case is the interesting one — a decimal `7656119…` and a Discord
 * snowflake are both 17–19 digit decimals, so the Steam prefix is checked first
 * and converted to the lowercase hex form FiveM actually reports.
 */
export function parsePaletteInput(raw: string): PaletteInput {
  const trimmed = raw.trim()
  if (!trimmed) return null

  const upper = trimmed.toUpperCase()
  if (BAN_CODE_PATTERN.test(upper)) return { kind: 'banCode', code: upper }

  if (trimmed.includes(':')) {
    const parsed = parseIdentifierInput(trimmed)
    if (!parsed) return null
    return { kind: 'identifier', ...parsed, expression: formatIdentifier(parsed.type, parsed.value) }
  }

  if (/^7656119\d{10}$/.test(trimmed)) {
    const hex = BigInt(trimmed).toString(16)
    return { kind: 'identifier', type: 'steam', value: hex, expression: `steam:${hex}` }
  }

  const parsed = parseIdentifierInput(trimmed)
  if (!parsed) return null
  return { kind: 'identifier', ...parsed, expression: formatIdentifier(parsed.type, parsed.value) }
}

/** Multi-line paste for the ban dialog: one identifier per line, invalid lines reported. */
export function parseIdentifierList(raw: string): {
  identifiers: Identifiers
  pairs: Array<{ type: IdentifierType; value: string }>
  invalid: string[]
} {
  const identifiers: Record<string, string> = {}
  const pairs: Array<{ type: IdentifierType; value: string }> = []
  const invalid: string[] = []

  for (const line of raw.split(/[\n,;]/)) {
    const candidate = line.trim()
    if (!candidate) continue
    const parsed = parsePaletteInput(candidate)
    if (!parsed || parsed.kind !== 'identifier') {
      invalid.push(candidate)
      continue
    }
    // First occurrence of a type wins, matching `parseIdentifiers` on the wire.
    if (identifiers[parsed.type] === undefined) {
      identifiers[parsed.type] = parsed.value
      pairs.push({ type: parsed.type, value: parsed.value })
    }
  }

  return { identifiers: identifiers as Identifiers, pairs, invalid }
}

export function hasHardIdentifier(identifiers: Identifiers): boolean {
  return HARD_IDENTIFIER_TYPES.some((type) => Boolean(identifiers[type]))
}

/** Flattens an `Identifiers` bag into the chip rows a table cell renders. */
export function identifierChips(
  identifiers: Identifiers | null | undefined,
  limit = 3,
): Array<{ type: IdentifierType; value: string }> {
  if (!identifiers) return []
  const chips: Array<{ type: IdentifierType; value: string }> = []
  for (const type of IDENTIFIER_TYPES) {
    const value = identifiers[type]
    if (value) chips.push({ type, value })
    if (chips.length >= limit) break
  }
  return chips
}

/** The `/lookup?identifier=` link target a chip's shift-click navigates to. */
export function lookupHref(type: ChipIdentifierType, value: string): string {
  return `/lookup?identifier=${encodeURIComponent(`${type}:${value}`)}`
}
